﻿import { BlobReader, BlobWriter, TextReader, ZipWriter } from '@zip.js/zip.js'
import { db } from '../db/catalogDb'
import type { CategoryRecord, ExportProgress, Id, ImageMetaRecord } from '../types/catalog'
import { sanitizeFileSegment } from '../utils/text'

interface ExportOptions {
  rootCategoryId?: Id | null
  onProgress?: (progress: ExportProgress) => void
}

function categoryPath(categoryId: Id, categories: Map<Id, CategoryRecord>): string[] {
  const result: string[] = []
  const visited = new Set<Id>()
  let current = categories.get(categoryId)

  while (current && !visited.has(current.id)) {
    visited.add(current.id)
    result.unshift(sanitizeFileSegment(current.name, 'category'))
    current = current.parentId ? categories.get(current.parentId) : undefined
  }
  return result
}

function isInsideRoot(image: ImageMetaRecord, rootCategoryId?: Id | null): boolean {
  return !rootCategoryId || image.categoryAncestors.includes(rootCategoryId)
}

function uniqueArchivePath(path: string, used: Set<string>): string {
  if (!used.has(path)) {
    used.add(path)
    return path
  }

  const dot = path.lastIndexOf('.')
  const base = dot > path.lastIndexOf('/') ? path.slice(0, dot) : path
  const extension = dot > path.lastIndexOf('/') ? path.slice(dot) : ''
  let index = 2
  let candidate = `${base} (${index})${extension}`
  while (used.has(candidate)) {
    index += 1
    candidate = `${base} (${index})${extension}`
  }
  used.add(candidate)
  return candidate
}

async function pickDestination(suggestedName: string): Promise<WritableStream<Uint8Array> | null> {
  if (!window.showSaveFilePicker) return null
  const handle = await window.showSaveFilePicker({
    suggestedName,
    types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }]
  })
  return handle.createWritable() as unknown as WritableStream<Uint8Array>
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export async function exportCatalogZip(options: ExportOptions = {}): Promise<void> {
  const categoryList = await db.categories.toArray()
  const categories = new Map(categoryList.map((category) => [category.id, category]))
  const total = options.rootCategoryId
    ? await db.images.where('categoryAncestors').equals(options.rootCategoryId).count()
    : await db.images.count()
  if (!total) throw new Error('No images to export')

  const date = new Intl.DateTimeFormat('sv-SE').format(new Date())
  const rootName = options.rootCategoryId
    ? sanitizeFileSegment(categories.get(options.rootCategoryId)?.name ?? 'category')
    : 'photo-vault'
  const fileName = `${rootName}-${date}.zip`

  let destination: WritableStream<Uint8Array> | null = null
  try {
    destination = await pickDestination(fileName)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return
    throw error
  }

  const blobWriter = destination ? null : new BlobWriter('application/zip')
  const writer = new ZipWriter(destination ?? blobWriter!, {
    zip64: true,
    bufferedWrite: true
  })
  const usedPaths = new Set<string>()
  let exported = 0
  let upperBound: [number, string] | null = null

  try {
    const manifest = {
      exportedAt: new Date().toISOString(),
      imageCount: total,
      categoryCount: categoryList.length,
      rootCategoryId: options.rootCategoryId ?? null,
      source: 'Pictalog',
      categories: categoryList.map((category) => ({
        id: category.id,
        name: category.name,
        parentId: category.parentId
      }))
    }
    await writer.add('catalog.json', new TextReader(JSON.stringify(manifest, null, 2)), { level: 6 })

    while (true) {
      // @ts-ignore
      const batch = upperBound
        ? await db.images.where('[updatedAt+id]').below(upperBound).reverse().limit(120).toArray()
        : await db.images.orderBy('[updatedAt+id]').reverse().limit(120).toArray()
      if (!batch.length) break
      // @ts-ignore
      const last = batch[batch.length - 1]
      upperBound = [last.updatedAt, last.id]

      for (const image of batch) {
        if (!isInsideRoot(image, options.rootCategoryId)) continue
        exported += 1
        options.onProgress?.({ current: exported, total, fileName: image.originalName })
        const binary = await db.imageFiles.get(image.id)
        if (!binary) continue
        const folders = categoryPath(image.categoryId, categories)
        const baseName = sanitizeFileSegment(image.title || image.originalName, 'image')
        const extension = sanitizeFileSegment(image.extension || 'img', 'img')
        const archivePath = uniqueArchivePath([...folders, `${baseName}.${extension}`].join('/'), usedPaths)
        await writer.add(archivePath, new BlobReader(binary.original), {
          level: 0,
          lastModDate: new Date(image.updatedAt)
        })
      }
    }

    const result = await writer.close()
    if (!destination && result instanceof Blob) downloadBlob(result, fileName)
  } catch (error) {
    try {
      await writer.close()
    } catch {
      // Ignora errori di chiusura dopo un fallimento dell'export.
    }
    throw error
  }
}
